import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, AlertTriangle, CalendarClock, Trash2 } from "lucide-react";

// Per-patient recalls, scheduled from the chair at the end of an appointment.
export default function RecallPanel({ patientId }) {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState({});
  const [type, setType] = useState("prophy");
  const [due, setDue] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [r, t] = await Promise.all([
        api.get(`/dental/patients/${patientId}/recalls`),
        api.get("/dental/recall-types"),
      ]);
      setRows(r.data || []);
      setTypes(t.data || {});
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId]);

  const add = async () => {
    setBusy(true);
    try {
      await api.post(`/dental/patients/${patientId}/recalls`, {
        recall_type: type,
        due_date: due || "",
        note,
      });
      setDue(""); setNote("");
      await load();
      toast.success("Recall scheduled");
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const close = async (id) => {
    setBusy(true);
    try { await api.put(`/dental/recalls/${id}`, { status: "completed" }); await load(); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this recall?")) return;
    setBusy(true);
    try { await api.delete(`/dental/recalls/${id}`); await load(); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const defaultMonths = types[type]?.default_interval_months;

  return (
    <Card className="p-4" data-testid="recall-panel">
      <h3 className="font-semibold text-slate-900 mb-3">Recalls</h3>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 p-3 bg-slate-50 rounded-md mb-3">
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="h-9 rounded-md border border-slate-300 px-2 text-sm"
          data-testid="recall-type"
        >
          {Object.entries(types).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="h-9" />
        <Input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="h-9" />
        <Button onClick={add} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-add-recall">
          <Plus size={14} className="mr-1" /> Schedule
        </Button>
      </div>
      <p className="text-[11px] text-slate-400 mb-3">
        Leave the date blank to use the default interval
        {defaultMonths != null ? ` (${defaultMonths} month${defaultMonths === 1 ? "" : "s"})` : ""}.
      </p>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-500">No recalls scheduled.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.id} className="py-2 flex items-center gap-3" data-testid={`patient-recall-${r.id}`}>
              {r.is_overdue
                ? <AlertTriangle size={15} className="text-red-600 shrink-0" />
                : <CalendarClock size={15} className="text-slate-400 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-900">
                  {r.type_label}
                  <span className="ml-2 text-xs text-slate-500">due {r.due_date}</span>
                  {r.status !== "open" ? <span className="ml-2 text-xs text-slate-400">· {r.status}</span> : null}
                </div>
                {r.note ? <div className="text-[11px] text-slate-400">{r.note}</div> : null}
              </div>
              {r.status === "open" || r.status === "scheduled" ? (
                <Button size="sm" variant="outline" onClick={() => close(r.id)} disabled={busy}>Close</Button>
              ) : null}
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(r.id)} disabled={busy}>
                <Trash2 size={13} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
